import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";

const ClassConversionMethodModal = ({ 
  show, 
  onHide, 
  schoolid, 
  getTeachersBySchoolId, 
  createClassConversionMethod,
  updateClassConversionMethod,
  getClassConversionMethodBySchoolId
}) => {
  const [teachers, setTeachers] = useState([]);
  const [selectedTransitionType, setSelectedTransitionType] = useState('');
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [showTeacherSelection, setShowTeacherSelection] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [existingRecord, setExistingRecord] = useState(null);
  const [existingTeacher, setExistingTeacher] = useState(null);

  // Reset modal state when modal opens/closes
  useEffect(() => {
    if (show) {
      setSelectedTransitionType('');
      setSelectedTeacher('');
      setShowTeacherSelection(false);
      setSubmitting(false);
      setError('');
      setSuccess('');
      setExistingRecord(null);
      setExistingTeacher(null);
      loadTeachers();
    }
  }, [show, schoolid]);

  // Check for existing records after teachers are loaded
  useEffect(() => {
    if (teachers.length > 0 && show) {
      checkExistingRecord();
    }
  }, [teachers, show]);

  const loadTeachers = async () => {
    try {
      setLoading(true);
      const teacherData = await getTeachersBySchoolId(schoolid);
      setTeachers(teacherData || []);
    } catch (error) {
      console.error("Error loading teachers:", error);
      setError("Failed to load teachers. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const checkExistingRecord = async () => {
    try {
      const existingRecord = await getClassConversionMethodBySchoolId(schoolid);
      
      // getClassConversionMethodBySchoolId now returns null for 404s
      if (existingRecord) {
        setExistingRecord(existingRecord);
        
        // Find the teacher who made the request
        const teacher = teachers.find(t => t.id === existingRecord.teacherid);
        if (!teacher && teachers.length > 0) {
          // If teachers are already loaded but teacher not found, it might be a different teacher
          setExistingTeacher({ name: `Teacher ID: ${existingRecord.teacherid}` });
        } else if (teacher) {
          setExistingTeacher(teacher);
        }
      } else {
        // No existing record found
        setExistingRecord(null);
        setExistingTeacher(null);
      }
    } catch (error) {
      console.error("Error checking existing records:", error);
      // Don't show error for this, as it's not critical
    }
  };

  const handleTransitionTypeSelect = (type) => {
    setSelectedTransitionType(type);
    setShowTeacherSelection(true);
    setError('');
  };

  const handleBack = () => {
    setShowTeacherSelection(false);
    setSelectedTransitionType('');
    setSelectedTeacher('');
    setError('');
  };

  const handleSubmit = async () => {
    if (!selectedTeacher) {
      setError("Please select a teacher.");
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      if (existingRecord) {
        // Update existing record
        await updateClassConversionMethod(
          schoolid,
          selectedTeacher,
          selectedTransitionType
        );
        setSuccess("Class conversion method successfully updated!");
      } else {
        // Create new record
        await createClassConversionMethod(
          selectedTeacher,
          schoolid,
          selectedTransitionType
        );
        setSuccess("Class conversion method successfully recorded!");
      }
      
      // Close modal after a short delay
      setTimeout(() => {
        onHide();
      }, 1500);
      
    } catch (error) {
      console.error("Error saving conversion method:", error);
      setError("Failed to save conversion method. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      onHide();
    }
  };

  const getTransitionTypeDisplay = (type) => {
    switch (type) {
      case 'direct':
        return 'Direct';
      case 'mix_reassign':
        return 'Mix & Reassign';
      case 'mix_reassign_received':
        return 'Mix & Reassign - REQUEST Received';
      default:
        return type;
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop="static"
      keyboard={false}
      centered
      size="lg"
    >
      <Modal.Header closeButton={!submitting}>
        <Modal.Title>
          {showTeacherSelection 
            ? `Select Teacher - ${getTransitionTypeDisplay(selectedTransitionType)}` 
            : "Mark Class Conversion Method"
          }
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" role="status" />
            <p className="mt-2">Loading teachers...</p>
          </div>
        ) : (
          <>
            {error && (
              <Alert variant="danger" className="mb-3">
                {error}
              </Alert>
            )}

            {success && (
              <Alert variant="success" className="mb-3">
                {success}
              </Alert>
            )}

            {existingRecord && (
              <Alert variant="info" className="mb-3">
                <strong>Note:</strong> This school already has a conversion method recorded:
                <br />
                <strong>{getTransitionTypeDisplay(existingRecord.transition_type)}</strong> - 
                Submitted on {new Date(existingRecord.date_submitted).toLocaleDateString()}
                {existingTeacher && (
                  <>
                    <br />
                    <strong>Requested by:</strong> {existingTeacher.name}
                    {existingTeacher.surname && ` ${existingTeacher.surname}`}
                    {existingTeacher.email && ` (${existingTeacher.email})`}
                  </>
                )}
                <br />
                <small>Selecting a new method will update the existing record for this school.</small>
              </Alert>
            )}

            {!showTeacherSelection ? (
              // Step 1: Transition Type Selection
              <>
                <p><strong>Select the class grouping method that was chosen:</strong></p>
                <p className="text-muted mb-4">
                  This will record which transition method the school selected for their Senior Infant classes.
                </p>

                <div className="d-grid gap-3">
                  <Button
                    variant="outline-primary"
                    size="lg"
                    onClick={() => handleTransitionTypeSelect('direct')}
                    disabled={submitting}
                    className="text-start p-3"
                  >
                    <div>
                      <strong>Direct</strong>
                      <div className="text-muted small">
                        Juniors stay in the same class groups in seniors.
                      </div>
                    </div>
                  </Button>

                  <Button
                    variant="outline-warning"
                    size="lg"
                    onClick={() => handleTransitionTypeSelect('mix_reassign')}
                    disabled={submitting}
                    className="text-start p-3"
                  >
                    <div>
                      <strong>Mix & Reassign</strong>
                      <div className="text-muted small">
                        Juniors DO NOT stay in the same class groups in seniors.
                      </div>
                    </div>
                  </Button>

                  <Button
                    variant="outline-info"
                    size="lg"
                    onClick={() => handleTransitionTypeSelect('mix_reassign_received')}
                    disabled={submitting}
                    className="text-start p-3"
                  >
                    <div>
                      <strong>Mix & Reassign - REQUEST Received</strong>
                      <div className="text-muted small">
                        Request for mix & reassign has been received and is being processed.
                      </div>
                    </div>
                  </Button>
                </div>
              </>
            ) : (
              // Step 2: Teacher Selection
              <>
                <p><strong>Select which teacher made this request:</strong></p>
                <p className="text-muted mb-3">
                  Method selected: <strong>{getTransitionTypeDisplay(selectedTransitionType)}</strong>
                  {existingRecord && (
                    <><br /><small>This will update the existing conversion method for this school.</small></>
                  )}
                </p>

                <Form.Group className="mb-3">
                  <Form.Label>Teacher</Form.Label>
                  <Form.Select
                    value={selectedTeacher}
                    onChange={(e) => setSelectedTeacher(e.target.value)}
                    disabled={submitting}
                  >
                    <option value="">Select a teacher...</option>
                    {teachers.map((teacher) => (
                      <option key={teacher.id} value={teacher.id}>
                        {teacher.name} {teacher.surname ? `${teacher.surname}` : ''}
                        {teacher.email ? ` (${teacher.email})` : ''}
                      </option>
                    ))}
                  </Form.Select>
                  {teachers.length === 0 && (
                    <Form.Text className="text-muted">
                      No teachers found for this school.
                    </Form.Text>
                  )}
                </Form.Group>

                <div className="d-flex gap-2">
                  <Button
                    variant="secondary"
                    onClick={handleBack}
                    disabled={submitting}
                  >
                    Back
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={submitting || !selectedTeacher}
                  >
                    {submitting ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          className="me-2"
                        />
                        Saving...
                      </>
                    ) : (
                      'Save Conversion Method'
                    )}
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ClassConversionMethodModal;